import React, { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-core';

import { ContentAudience } from '@amzn/awscat-pattern-library-service-model';

import ContentAudienceBadge from '../../workflow-viewer/ContentAudienceBadge';
import { BatchUnpublishPageViewModel, ContentItem } from './BatchUnpublishPageViewModel';

interface ConfirmationStepViewProps {
  readonly vm: BatchUnpublishPageViewModel;
}

export const ConfirmationStepView = memo(function ConfirmationStepView({
  vm,
}: ConfirmationStepViewProps) {
  return (
    <SpaceBetween size="m">
      {vm.fetchErrors.length > 0 && (
        <Alert type="warning" header="Some IDs could not be resolved">
          <SpaceBetween size="xxs">
            {vm.fetchErrors.map((err) => (
              <Box key={err.contentId} variant="code">
                {err.contentId} — {err.reason}
              </Box>
            ))}
          </SpaceBetween>
        </Alert>
      )}

      <Table
        selectionType="multi"
        selectedItems={vm.selectedItems as ContentItem[]}
        onSelectionChange={({ detail }) => vm.setSelectedItems(detail.selectedItems)}
        trackBy="contentId"
        items={vm.items as ContentItem[]}
        isItemDisabled={(item) => item.contentAudience !== ContentAudience.INTERNAL}
        columnDefinitions={[
          { id: 'title', header: 'Title', cell: (item) => item.title, sortingField: 'title' },
          { id: 'author', header: 'Author', cell: (item) => item.author, sortingField: 'author' },
          {
            id: 'audience',
            header: 'Audience',
            cell: (item) => <ContentAudienceBadge contentAudience={item.contentAudience} />,
          },
          {
            id: 'state',
            header: 'State',
            cell: (item) => item.publicationState,
            sortingField: 'publicationState',
          },
          { id: 'contentId', header: 'Content ID', cell: (item) => item.contentId },
        ]}
        header={
          <Header variant="h2" counter={`(${vm.selectedItems.length}/${vm.items.length})`}>
            Content to unpublish
          </Header>
        }
        empty={<Box textAlign="center">No valid content items found.</Box>}
      />

      <SpaceBetween direction="horizontal" size="s">
        <Button onClick={vm.goBack}>Go back</Button>
        <Button
          variant="primary"
          disabled={vm.selectedItems.length === 0}
          onClick={vm.confirmUnpublish}
        >
          Unpublish {vm.selectedItems.length} item{vm.selectedItems.length !== 1 ? 's' : ''}
        </Button>
      </SpaceBetween>
    </SpaceBetween>
  );
});
